import { useMemo } from "react";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { AuthOnFile } from "../../../data-model/medicalPaOrdersModel";
import { WorkflowConfig } from "../../../data-model/workflowConfig";
import { MedicalPaOrdersAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import { useSubmissionIsPresent } from "./useSubmissionIsPresent";

interface FilingButtonState {
  shouldShowStartFilingButton: boolean;
  startFilingButtonText: string;
}

/**
 * Checks if a submission is present and returns the appropriate button state
 */
const getSubmissionButtonState = (
  submissionIsPresent: boolean,
): FilingButtonState | null => {
  if (submissionIsPresent) {
    return {
      shouldShowStartFilingButton: true,
      startFilingButtonText: "View Submission",
    };
  }
  return null;
};

/**
 * Checks if the order is eligible for filing based on workflow config and auth status
 */
const isEligibleForFiling = (
  workflowConfig: WorkflowConfig,
  authOnFile: AuthOnFile,
): boolean => {
  const allowedAuthStatuses = [
    MedicalPaOrdersAuthStatus.AuthRequired,
    MedicalPaOrdersAuthStatus.NoAuthRequired,
  ];
  const isWorkflowConfigValid =
    workflowConfig &&
    workflowConfig?.submission_form_config_id &&
    workflowConfig?.submission_form_config_id !== "";
  const isAuthOnFileValid =
    authOnFile &&
    allowedAuthStatuses.includes(
      authOnFile?.auth_status as MedicalPaOrdersAuthStatus,
    );
  return !!(isWorkflowConfigValid && isAuthOnFileValid);
};

/**
 * Gets the button state for starting a new filing
 */
const getStartFilingButtonState = (
  workflowConfig: WorkflowConfig,
  authOnFile: AuthOnFile,
): FilingButtonState | null => {
  if (isEligibleForFiling(workflowConfig, authOnFile)) {
    return {
      shouldShowStartFilingButton: true,
      startFilingButtonText: "Start Filing",
    };
  }
  return null;
};

/**
 * Gets the default button state when no other conditions are met
 */
const getDefaultButtonState = (): FilingButtonState => {
  return {
    shouldShowStartFilingButton: false,
    startFilingButtonText: "Go Back",
  };
};

/**
 * Determines whether the "Start Filing" / "View Submission" button should
 * appear and which label to display, based on submission state and
 * workflow configuration eligibility.
 *
 * @returns Object with `shouldShowStartFilingButton` and `startFilingButtonText`
 */
export const useShouldStartFilingAndButtonText = () => {
  const { workflowConfig, authOnFileDetails } =
    useDataAccumulatorForSingleOrder();

  const submissionIsPresent = useSubmissionIsPresent();

  const shouldShowStartFilingButtonAndText = useMemo(() => {
    // Check for existing submission first
    const submissionState = getSubmissionButtonState(submissionIsPresent);
    if (submissionState) return submissionState;

    // Check if eligible for new filing
    const startFilingState = getStartFilingButtonState(
      workflowConfig ?? {},
      authOnFileDetails?.auth_on_file ?? {},
    );
    if (startFilingState) return startFilingState;

    // Default state
    return getDefaultButtonState();
  }, [workflowConfig, authOnFileDetails, submissionIsPresent]);

  return shouldShowStartFilingButtonAndText;
};
